/* eslint-disable react-hooks/exhaustive-deps */
import HighchartsReact from 'highcharts-react-official';
import HighStock, { AxisSetExtremesEventObject } from 'highcharts/highstock';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import _ from 'lodash';
import {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    Ref,
    Dispatch,
    SetStateAction,
} from 'react';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import { rebaseGrowthChart } from '../utils';
import DatePicker, { Date as DateType } from './DatePicker';
import { DatePickerOptions, highChartOptions, toolTipCustomOptions } from './GrowthChartWrapper';
import IntegratedDatePicker from './IntegratedDatePicker';
import CustomDateRangePicker from './CustomDateRangePicker';

if (typeof window !== `undefined`) {
    highchartsAccessibility(HighStock);
}

export interface IntegratedDatePickerOptions {
    datePickerIconPosition?: string;
    datePickerIconType?: 'FontAwesome Icon' | 'Image';
    dateFormat?: string;
    startDateLabel?: string;
    endDateLabel?: string;
    datePickerIcon?: string;
    datePickerImage?: string;
}

export interface CustomDatePickerOptions {
    startDateFilterByKey?: string;
    endDateFilterByKey?: string;
    datePickerIconPosition?: 'Right' | 'Left';
    datePickerIconType?: 'FontAwesome Icon' | 'Image';
    datePickerIcon?: string;
    datePickerImage?: string;
    closeDatePickerIcon?: string;
    closeDatePickerIconPosition?: 'Right' | 'Left';
    closeDatePickerIconType?: 'FontAwesome Icon' | 'Image';
    closeDatePickerImage?: string;
    backArrowIconType?: 'FontAwesome Icon' | 'Image';
    backArrowIcon?: string;
    backArrowIconPosition?: string;
    backArrowImage?: string;
    forwardArrowIcon?: string;
    forwardArrowImage?: string;
    forwardArrowIconPosition?: string;
    forwardArrowIconType?: 'FontAwesome Icon' | 'Image';
    startDatelabel?: string;
    endDatelabel?: string;
    dataKey?: string;
    limitStartDateMinToMinDate?: boolean;
    limitStartDateMaxToCurrentDate?: boolean;
    limitStartDateMaxToMaxDate?: boolean;
    limitEndDateMinToStartDate?: boolean;
    limitEndDateMaxToCurrentDate?: boolean;
    limitCurrentDaySelection?: boolean;
    limitEndDateMaxToMaxDate?: boolean;
    startDateErrorMessage?: string;
    endDateErrorMessage?: string;
    errorIconPosition?: 'Right' | 'Left';
    errorIconType?: 'FontAwesome Icon' | 'Image';
    onChangeDateRange: (
        minDate: any,
        maxDate: any,
        filterBy?: string,
        analyticsLabel?: string,
        startDateFilterByKey?: string,
        endDateFilterByKey?: string
    ) => void;
    limitDatekeySelection?: string;
    limitDateStartErrorMessage?: string;
    limitDateEndErrorMessage?: string;
}

export interface GrowthChartProps {
    seriesData: any;
    highChartOptions: HighStock.Options;
    langOptions: highChartOptions;
    showTotalReturns: boolean;
    showInvestmentAmount: boolean;
    setTotalReturnRef: React.Ref<HTMLElement>;
    datePickerOptions: DatePickerOptions;
    growthAmountLabel: string;
    initialGrowthAmount: number;
    growthAmountFeedback: string;
    totalReturnsLabel: string;
    showDateInput: boolean;
    altText?: string;
    extremes: AxisSetExtremesEventObject;
    setInvestmentAmount: Dispatch<SetStateAction<number>>;
    investmentAmount: number;
    datePicker: 'Isolated Date Picker' | 'Integrated Date Picker' | 'Custom Date Picker';
    integratedDatePickerOptions: IntegratedDatePickerOptions;
    customDatePickerOptions: CustomDatePickerOptions;
    constructorType: string;
    tooltipOptions?: toolTipCustomOptions[];
    selectedRange?: number;
    resetSelectedRange?: () => void;
    yAxisValueOverride: boolean;
}

interface PropsShape extends commonTypes.WidgetInstanceProps, GrowthChartProps {}

function GrowthChart(props: PropsShape, ref: Ref<any>) {
    const {
        data,
        seriesData,
        highChartOptions,
        showInvestmentAmount,
        setInvestmentAmount,
        investmentAmount,
        showTotalReturns,
        datePickerOptions,
        altText,
        yAxisValueOverride,
        ...rest
    } = props;

    const [growthAmountFeedback, setGrowthAmountFeedback] = useState<string>('');
    const [chartData, setChartData] = useState(seriesData);

    const growthChartRef = useRef<any>(null);
    const chart = growthChartRef?.current?.chart;
    const xExtremes = rest.extremes;
    const minDate = xExtremes?.dataMin;
    const maxDate = xExtremes?.dataMax;

    let chartMetaData;

    const i18n = commonHooks.useLocalization();

    if (_.isArray(chart?.xAxis) && _.isArray(chart?.yAxis) && _.isArray(seriesData[0].data)) {
        // eslint-disable-next-line max-len
        const _xAxisEndPoint = chart?.xAxis[0].paddedTicks[chart?.xAxis[0].paddedTicks.length - 1];
        const _xAxisStartPoint = chart?.xAxis[0].paddedTicks[0];

        chartMetaData = {
            xAxisStartPoint: new Date(_xAxisStartPoint).getFullYear(),
            xAxisEndPoint: new Date(_xAxisEndPoint).getFullYear(),
            yAxisStartPoint: chart?.yAxis[0].paddedTicks[0],
            yAxisEndPoint: chart?.yAxis[0].paddedTicks[chart?.yAxis[0].paddedTicks.length - 1],
            endGrowthValue: i18n.t(seriesData[0].data[seriesData[0].data.length - 1].y, 'curMax2'),
        };
    }

    const _altText = i18n.format(altText || '', { ...data, ...chartMetaData });
    const __altText = i18n.format(_altText || '', { ...data, ...chartMetaData });

    useEffect(() => {
        const langOptions = props?.langOptions?.value ? JSON.parse(props?.langOptions?.value) : {};
        // render few chart options on initial render
        HighStock.setOptions({
            lang: {
                rangeSelectorZoom: '',
                rangeSelectorFrom: '',
                ...langOptions,
            },
        });
    }, []);

    useEffect(() => {
        updateChart();
        setGrowthAmountFeedback('');
    }, [investmentAmount]);

    useImperativeHandle(ref, () => ({
        updateGrowthChart(xExtremeMin?: number) {
            updateChart(xExtremeMin);
        },
    }));

    const updateChart = (xExtremeMin?: number) => {
        const xExtremes = growthChartRef?.current?.chart?.xAxis?.[0]?.getExtremes();
        let minX: number = xExtremes?.userMin || 0;

        if (xExtremeMin) {
            minX = xExtremeMin;
        }

        seriesData
            .filter((s: any) => s.type !== 'flags')
            .forEach((series: any, i: number) => {
                const firstPointIndex = series.data.findIndex((point: any) => point.x >= minX);
                // get the first point after adjust the nav
                const firstPoint = series.data[firstPointIndex];

                if (firstPoint === undefined) return;

                const rebasedData = rebaseGrowthChart(
                    investmentAmount,
                    series.data,
                    firstPoint,
                    rest.tooltipOptions,
                    i,
                    yAxisValueOverride
                ).slice();

                // update the chart with the rebased values
                growthChartRef?.current?.chart?.series[i].update({ data: rebasedData });
                setChartData((prevChartData: any) =>
                    prevChartData?.map((s: any, index: number) => (index === i ? rebasedData : s))
                );
            });
    };

    const onDateChange = (startDate: DateType, endDate: DateType) => {
        // reset the selected range to avoid conflict between manual change and chart options
        if (rest?.resetSelectedRange) {
            rest.resetSelectedRange();
        }
        chart?.xAxis?.[0]?.setExtremes(
            Date.UTC(startDate.year, startDate.month, startDate.day),
            Date.UTC(endDate.year, endDate.month, endDate.day)
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input: number = parseFloat(e?.target?.value);

        if (input >= 0.01) {
            setInvestmentAmount(input);
        } else if (input < 0.01) {
            // 0.01 is an agreed min value for validation representing 1 cent
            setGrowthAmountFeedback(i18n.t('widgets_growthChart_inputError'));
            setInvestmentAmount(rest.initialGrowthAmount);
        } else {
            setGrowthAmountFeedback('');
            setInvestmentAmount(rest.initialGrowthAmount);
        }
    };

    return (
        <div id="growth-chart" className="growth-chart">
            {showInvestmentAmount && (
                <div className={'investment-amount'}>
                    <label
                        htmlFor={'growth-amount-input'}
                        className={'growth-chart-label'}
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(rest.growthAmountLabel, data),
                        }}
                    />
                    <input
                        className={'growth-amount-input'}
                        id={'growth-amount-input'}
                        type={'number'}
                        min={0.01}
                        onChange={handleInputChange}
                        placeholder={`${rest.initialGrowthAmount}`}
                    />
                    <div className="growth-amount-feedback">{growthAmountFeedback}</div>
                </div>
            )}
            {/* (!rest?.datePicker) condition is for pre-existing templates */}
            {/* we can remove this once we make changes to all those templates */}
            {rest.showDateInput &&
                minDate &&
                maxDate &&
                (!rest?.datePicker || rest?.datePicker === 'Isolated Date Picker' ? (
                    <DatePicker
                        data={data}
                        minDate={minDate}
                        maxDate={maxDate}
                        extremes={rest.extremes}
                        startDateLabel={datePickerOptions?.startDateLabel}
                        endDateLabel={datePickerOptions?.endDateLabel}
                        startDayAriaLabel={datePickerOptions?.startDateLabel}
                        startMonthAriaLabel={datePickerOptions?.startMonthAriaLabel}
                        startYearAriaLabel={datePickerOptions?.startYearAriaLabel}
                        endDayAriaLabel={datePickerOptions?.endDayAriaLabel}
                        endMonthAriaLabel={datePickerOptions?.endMonthAriaLabel}
                        endYearAriaLabel={datePickerOptions?.endYearAriaLabel}
                        onDateChange={onDateChange}
                    />
                ) : rest?.datePicker === 'Integrated Date Picker' ? (
                    <IntegratedDatePicker
                        data={data}
                        minDate={minDate}
                        maxDate={maxDate}
                        startDateLabel={rest?.integratedDatePickerOptions?.startDateLabel}
                        endDateLabel={rest?.integratedDatePickerOptions?.endDateLabel}
                        dateFormat={rest?.integratedDatePickerOptions?.dateFormat}
                        datePickerIconPosition={
                            rest?.integratedDatePickerOptions?.datePickerIconPosition
                        }
                        datePickerIconType={rest?.integratedDatePickerOptions?.datePickerIconType}
                        datePickerIcon={rest?.integratedDatePickerOptions?.datePickerIcon}
                        datePickerImage={rest?.integratedDatePickerOptions?.datePickerImage}
                        onChangeDateRange={onDateChange}
                    />
                ) : (
                    rest?.datePicker === 'Custom Date Picker' && (
                        <CustomDateRangePicker
                            data={data}
                            onChangeDateRange={onDateChange}
                            minDate={minDate}
                            maxDate={maxDate}
                            extremes={rest.extremes}
                            startDatelabel={rest?.customDatePickerOptions?.startDatelabel}
                            endDatelabel={rest?.customDatePickerOptions?.endDatelabel}
                            datePickerIconPosition={
                                rest?.customDatePickerOptions?.datePickerIconPosition
                            }
                            datePickerIconType={rest?.customDatePickerOptions?.datePickerIconType}
                            datePickerIcon={rest?.customDatePickerOptions?.datePickerIcon}
                            datePickerImage={rest?.customDatePickerOptions?.datePickerImage}
                            closeDatePickerIcon={rest?.customDatePickerOptions?.closeDatePickerIcon}
                            closeDatePickerIconPosition={
                                rest?.customDatePickerOptions?.closeDatePickerIconPosition
                            }
                            closeDatePickerIconType={
                                rest?.customDatePickerOptions?.closeDatePickerIconType
                            }
                            closeDatePickerImage={
                                rest?.customDatePickerOptions?.closeDatePickerImage
                            }
                            backArrowIconType={rest?.customDatePickerOptions?.backArrowIconType}
                            backArrowIcon={rest?.customDatePickerOptions?.backArrowIcon}
                            backArrowImage={rest?.customDatePickerOptions?.backArrowImage}
                            forwardArrowIcon={rest?.customDatePickerOptions?.forwardArrowIcon}
                            forwardArrowImage={rest?.customDatePickerOptions?.forwardArrowImage}
                            forwardArrowIconType={
                                rest?.customDatePickerOptions?.forwardArrowIconType
                            }
                            limitStartDateMinToMinDate={
                                rest?.customDatePickerOptions?.limitStartDateMinToMinDate
                            }
                            limitStartDateMaxToCurrentDate={
                                rest?.customDatePickerOptions?.limitStartDateMaxToCurrentDate
                            }
                            limitStartDateMaxToMaxDate={
                                rest?.customDatePickerOptions?.limitStartDateMaxToMaxDate
                            }
                            limitEndDateMinToStartDate={
                                rest?.customDatePickerOptions?.limitEndDateMinToStartDate
                            }
                            limitEndDateMaxToCurrentDate={
                                rest?.customDatePickerOptions?.limitEndDateMaxToCurrentDate
                            }
                            limitCurrentDaySelection={
                                rest?.customDatePickerOptions?.limitCurrentDaySelection
                            }
                            limitEndDateMaxToMaxDate={
                                rest?.customDatePickerOptions?.limitEndDateMaxToMaxDate
                            }
                            startDateErrorMessage={
                                rest?.customDatePickerOptions?.startDateErrorMessage
                            }
                            endDateErrorMessage={rest?.customDatePickerOptions?.endDateErrorMessage}
                            errorIconPosition={rest?.customDatePickerOptions?.errorIconPosition}
                            errorIconType={rest?.customDatePickerOptions?.errorIconType}
                            limitDatekeySelection={
                                rest?.customDatePickerOptions?.limitDatekeySelection
                            }
                            limitDateStartErrorMessage={
                                rest?.customDatePickerOptions?.limitDateStartErrorMessage
                            }
                            limitDateEndErrorMessage={
                                rest?.customDatePickerOptions?.limitDateEndErrorMessage
                            }
                            staticDataTabIndex={0}
                        />
                    )
                ))}
            {showTotalReturns && (
                <div className="total-returns">
                    <label
                        className={'total-returns-label'}
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(rest.totalReturnsLabel, data),
                        }}
                    />
                    <br />
                    <b ref={rest.setTotalReturnRef} />
                </div>
            )}
            <figcaption
                className={`sr-only`}
                dangerouslySetInnerHTML={{ __html: __altText || '' }}
            />

            {chartData?.length && (
                <HighchartsReact
                    ref={growthChartRef}
                    highcharts={HighStock}
                    options={{ ...highChartOptions, series: chartData }}
                    constructorType={
                        rest.constructorType === ''
                            ? ''
                            : rest.constructorType === undefined
                              ? 'stockChart'
                              : rest.constructorType
                    }
                    allowChartUpdate={true}
                    oneToOne={true}
                    updateArgs={[true, true, true]}
                />
            )}
        </div>
    );
}

export default forwardRef(GrowthChart);
